import React from 'react';
import { motion } from 'motion/react';
import { Target, Eye, Heart, ShieldCheck, Users, Award, History, BookOpen } from 'lucide-react';

export const About = () => {
  return (
    <div className="pb-32">
      {/* Header */}
      <section className="bg-white py-24 border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl space-y-6">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-7xl font-bold text-primary tracking-tight"
            >
              Nossa <span className="text-secondary italic">Associação</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-slate-600 leading-relaxed"
            >
              Conheça a trajetória e os valores da Associação De Pais E Mestres 8 De Maio.
            </motion.p>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="container mx-auto px-4 md:px-6 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 text-secondary font-bold uppercase tracking-widest text-sm">
              <History size={18} />
              Desde 1960
            </div>
            <h2 className="text-4xl font-bold text-primary tracking-tight">Nossa História</h2>
            <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
              <p>
                Por volta de 1960 foi fundado o Grupo Educacional Doutor Fernando Corrêa da Costa, em homenagem ao médico e político mato-grossense que ocupou os cargos de senador e governador.
              </p>
              <p>
                Em 1971, houve a doação do terreno para a construção do prédio escolar. Em 22 de outubro de 1980, foi criada a Escola Estadual de 1º e 2º graus 8 de Maio, nome que faz referência ao aniversário do município de Iguatemi.
              </p>
              <p>
                Em 12 de maio de 1998, passou a denominar-se oficialmente Escola Estadual 8 de Maio. Nosso brasão, adotado em 2016, foi criado pela estudante Adrieli Silva de Oliveira, simbolizando a força e a união da nossa comunidade escolar.
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] bg-slate-100 rounded-3xl overflow-hidden shadow-2xl border-8 border-white">
              <img
                src="https://i.postimg.cc/vTnfhcVH/Whats-App-Image-2026-03-12-at-18-54-14.jpg"
                alt="Bandeira da Escola no Programa Agrinho"
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
            <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-primary rounded-2xl flex items-center justify-center text-white text-center p-6 shadow-2xl">
              <span className="font-bold text-lg leading-tight">Reconhecimento e Tradição</span>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="bg-primary py-32 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            <div className="space-y-6">
              <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center text-white">
                <Target size={32} />
              </div>
              <h3 className="text-3xl font-bold">Missão</h3>
              <p className="text-blue-100 text-lg leading-relaxed">
                Contribuir para a formação de cidadãos críticos e conscientes, capazes de atuar como agentes de transformação na realidade onde estão inseridos.
              </p>
            </div>
            <div className="space-y-6">
              <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center text-white">
                <Eye size={32} />
              </div>
              <h3 className="text-3xl font-bold">Visão</h3>
              <p className="text-blue-100 text-lg leading-relaxed">
                Desenvolver o senso crítico, moral e cooperativo, incentivando nossos estudantes na construção de uma sociedade igualitária e solidária.
              </p>
            </div>
            <div className="space-y-6">
              <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center text-white">
                <Heart size={32} />
              </div>
              <h3 className="text-3xl font-bold">Valores</h3>
              <ul className="space-y-4 text-blue-100 text-lg">
                <li className="flex items-center gap-3"><ShieldCheck size={20} className="text-secondary" /> Competência Profissional</li>
                <li className="flex items-center gap-3"><Users size={20} className="text-secondary" /> Diálogo e Confiança</li>
                <li className="flex items-center gap-3"><Award size={20} className="text-secondary" /> Respeito e Ética</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Content */}
      <section className="container mx-auto px-4 md:px-6 py-32">
        <div className="max-w-4xl mx-auto space-y-16">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-primary">Quem Somos</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              A Escola Estadual 8 de Maio intensifica atividades que proporcionam o desenvolvimento das habilidades cognitivas, relações interpessoais e o protagonismo juvenil, possibilitando um ambiente propício a transmitir e adquirir conhecimento e formar cidadãos.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Entendemos que o estudante necessita da presença do professor, do diálogo, do olho no olho, da atenção e do reconhecimento. Valorizamos a argumentação sólida na solução de problemas e encaramos cada estudante como um sujeito capaz de participação ativa.
            </p>
          </div>

          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-primary">Objetivos Estratégicos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                'Melhorar o desempenho acadêmico',
                'Reforçar o trabalho em equipe',
                'Aumentar a taxa de aprovação',
                'Envolvimento da família e comunidade',
                'Parcerias com instituições locais',
                'Diminuir a evasão escolar'
              ].map((obj, i) => (
                <div key={i} className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <BookOpen size={20} className="text-secondary" />
                  <span className="font-medium text-slate-700">{obj}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
